import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { useUser } from './useUser';

interface UserWithRoleData {
  user: User | null;
  userRole: string | null;
  isLoading: boolean;
}

/**
 * Enhanced version of useUser that also fetches the user's role
 */
export const useUserWithRole = (): UserWithRoleData => {
  const { user, loading } = useUser();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Skip if user is still loading or no user
    if (loading || !user) {
      setIsLoading(loading);
      return;
    }

    const fetchUserRole = async () => {
      try {
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error fetching user role:', error);
          setUserRole(null);
        } else {
          // Convert role from database format (e.g., "admin") to component expected format (e.g., "Admin")
          const roleFromDB = data?.role || null;
          if (roleFromDB) {
            // Handle special case for "superadmin" which should be "SuperAdmin"
            if (roleFromDB.toLowerCase() === 'superadmin') {
              setUserRole('SuperAdmin');
            } 
            // Handle admin, owner and other standard roles
            else {
              // Convert snake_case to PascalCase (e.g. "project_manager" becomes "ProjectManager")
              const normalizedRole = roleFromDB
                .split('_')
                .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join('');
              setUserRole(normalizedRole);
            }
          } else {
            setUserRole(null);
          }
        }
      } catch (err) {
        console.error('Unexpected error fetching user role:', err);
        setUserRole(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserRole();
  }, [user, loading]);

  return { user, userRole, isLoading };
};
